import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clapperboard,
  GitBranch,
  Loader2,
  MessageSquare,
  MonitorPlay,
  Play,
  RadioTower,
  RotateCcw,
  Sparkles,
  Video,
} from "lucide-react";
import { Badge } from "@/components/primitives";
import {
  DoomUnityArena,
  type DoomChoiceOption,
} from "@/components/next-gen-video-lesson/DoomUnityArena";
import { cosmosApiUrl } from "@/lib/cosmos-api-base-url";
import { cn } from "@/lib/utils";

type UnityStatus = "checking" | "loading" | "ready" | "missing" | "error";

type UnityConfig = {
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streamingAssetsUrl: string;
  companyName: string;
  productName: string;
  productVersion: string;
};

type UnityInstance = {
  Quit?: () => Promise<void>;
};

type CreateUnityInstance = (
  canvas: HTMLCanvasElement,
  config: UnityConfig,
  onProgress?: (progress: number) => void,
) => Promise<UnityInstance>;

type VideoNode = {
  id: string;
  type: "video";
  label: string;
  title: string;
  subtitle: string;
  fileName: string;
  nextNodeId: string;
};

type QuestionNode = {
  id: string;
  type: "question";
  label: string;
  title: string;
  prompt: string;
  options: Array<{
    label: string;
    score: number;
    tone: string;
    feedbackTitle: string;
    feedbackBody: string;
    nextNodeId: string;
  }>;
};

type SummaryNode = {
  id: string;
  type: "summary";
  title: string;
};

type EpisodeNode = VideoNode | QuestionNode | SummaryNode;

type ChoiceResult = {
  label: string;
  score: number;
  tone: string;
  feedbackTitle: string;
  feedbackBody: string;
};

type ChoiceMode = "classic" | "doom";

declare global {
  interface Window {
    createUnityInstance?: CreateUnityInstance;
  }
}

const unityBasePath = "/unity/next-gen-video-lesson";
const unityBuildName = "next-gen-video-lesson";
const unityManifestUrl = `${unityBasePath}/manifest.json`;
const unityLoaderUrl = `${unityBasePath}/Build/${unityBuildName}.loader.js`;

const unityConfig: UnityConfig = {
  dataUrl: `${unityBasePath}/Build/${unityBuildName}.data`,
  frameworkUrl: `${unityBasePath}/Build/${unityBuildName}.framework.js`,
  codeUrl: `${unityBasePath}/Build/${unityBuildName}.wasm`,
  streamingAssetsUrl: `${unityBasePath}/StreamingAssets`,
  companyName: "AIHR",
  productName: "Next Gen Video Lesson",
  productVersion: "0.1.0-poc",
};

const videoPath = (fileName: string) => cosmosApiUrl(`/poc-videos/${encodeURIComponent(fileName)}`);

const episodeNodes: EpisodeNode[] = [
  {
    id: "scenario-1",
    type: "video",
    label: "Scenario 01",
    title: "The dilemma appears",
    subtitle: "Watch the setup. Your first decision sets the tone.",
    fileName: "[forlearnosity]hrcs-ethical_dilemmas_s1_v1 (1080p).mp4",
    nextNodeId: "question-1",
  },
  {
    id: "question-1",
    type: "question",
    label: "Decision 01",
    title: "Choose your opening move",
    prompt: "What is the best first response to this ethical dilemma?",
    options: [
      {
        label: "Pause and ask clarifying questions before acting.",
        score: 2,
        tone: "Composed",
        feedbackTitle: "Best read of the room",
        feedbackBody: "You bought time, protected trust, and opened space for better evidence.",
        nextNodeId: "feedback-1",
      },
      {
        label: "Escalate immediately with the facts you have.",
        score: 1,
        tone: "Decisive",
        feedbackTitle: "Fast, but brittle",
        feedbackBody: "You acted with urgency, but the story may punish a thin fact pattern.",
        nextNodeId: "feedback-1",
      },
      {
        label: "Ignore the issue until someone else raises it.",
        score: 0,
        tone: "Avoidant",
        feedbackTitle: "Risk accepted",
        feedbackBody: "The silence keeps things calm for now, but consequence pressure is building.",
        nextNodeId: "feedback-1",
      },
    ],
  },
  {
    id: "feedback-1",
    type: "video",
    label: "Feedback 01",
    title: "Consequence check",
    subtitle: "The story reacts to your opening instinct.",
    fileName: "[forlearnosity]hrcs-ethical_dilemmas_s1_answer_v1 (1080p).mp4",
    nextNodeId: "scenario-2",
  },
  {
    id: "scenario-2",
    type: "video",
    label: "Scenario 02",
    title: "The pressure rises",
    subtitle: "A new layer appears. Keep track of the trade-offs.",
    fileName: "[forlearnosity]hrcs-ethical_dilemmas_s2_v1 (1080p).mp4",
    nextNodeId: "question-2",
  },
  {
    id: "question-2",
    type: "question",
    label: "Decision 02",
    title: "Pick your priority",
    prompt: "What should the learner prioritize next?",
    options: [
      {
        label: "Balance policy, impact, and stakeholder context.",
        score: 2,
        tone: "Balanced",
        feedbackTitle: "High-integrity move",
        feedbackBody: "You are weighing the policy and human impact before the scene escalates.",
        nextNodeId: "feedback-2",
      },
      {
        label: "Choose the fastest path even if the impact is unclear.",
        score: 1,
        tone: "Pragmatic",
        feedbackTitle: "Momentum gained",
        feedbackBody: "You kept the episode moving, but speed may create a blind spot.",
        nextNodeId: "feedback-2",
      },
      {
        label: "Defer the decision without explaining why.",
        score: 0,
        tone: "Passive",
        feedbackTitle: "Control slipping",
        feedbackBody: "The delay creates uncertainty, and the scenario starts filling that gap for you.",
        nextNodeId: "feedback-2",
      },
    ],
  },
  {
    id: "feedback-2",
    type: "video",
    label: "Feedback 02",
    title: "Impact revealed",
    subtitle: "The scenario shows what your decision unlocks.",
    fileName: "[forlearnosity]hrcs-ethical_dilemmas_s2_answer_v1 (1080p).mp4",
    nextNodeId: "scenario-3",
  },
  {
    id: "scenario-3",
    type: "video",
    label: "Scenario 03",
    title: "The final call",
    subtitle: "Your last choice determines the debrief.",
    fileName: "[forlearnosity]hrcs-ethical_dilemmas_s3_v1 (1080p).mp4",
    nextNodeId: "question-3",
  },
  {
    id: "question-3",
    type: "question",
    label: "Decision 03",
    title: "Close the loop",
    prompt: "How should the learner close the situation?",
    options: [
      {
        label: "Document the reasoning and communicate next steps.",
        score: 2,
        tone: "Accountable",
        feedbackTitle: "Clean finish",
        feedbackBody: "You made the reasoning visible and gave everyone a clear path forward.",
        nextNodeId: "feedback-3",
      },
      {
        label: "Only tell the most senior stakeholder.",
        score: 1,
        tone: "Selective",
        feedbackTitle: "Partial closure",
        feedbackBody: "You informed power, but the wider trust loop may still be unresolved.",
        nextNodeId: "feedback-3",
      },
      {
        label: "Move on without closing the loop.",
        score: 0,
        tone: "Unresolved",
        feedbackTitle: "Loose end created",
        feedbackBody: "The story keeps emotional debt on the board because nobody gets closure.",
        nextNodeId: "feedback-3",
      },
    ],
  },
  {
    id: "feedback-3",
    type: "video",
    label: "Feedback 03",
    title: "Debrief and score",
    subtitle: "Final consequences before the summary.",
    fileName: "[forlearnosity]hrcs-ethical_dilemmas_s3_answer_v1 (1080p).mp4",
    nextNodeId: "summary",
  },
  {
    id: "summary",
    type: "summary",
    title: "Your ethical judgment profile",
  },
];

const episodeMap = new Map(episodeNodes.map((node) => [node.id, node]));

type DecisionTreeLevel = {
  depth: number;
  scenario: VideoNode;
  question: QuestionNode;
  feedback: VideoNode;
};

const decisionTreeLevels: DecisionTreeLevel[] = (() => {
  const levels: DecisionTreeLevel[] = [];
  let scenarioId: string | null = "scenario-1";

  for (let depth = 1; depth <= 3 && scenarioId; depth += 1) {
    const scenario = episodeMap.get(scenarioId);
    if (!scenario || scenario.type !== "video") {
      break;
    }

    const question = episodeMap.get(scenario.nextNodeId);
    if (!question || question.type !== "question") {
      break;
    }

    const feedbackId = question.options[0]?.nextNodeId;
    const feedback = feedbackId ? episodeMap.get(feedbackId) : undefined;
    if (!feedback || feedback.type !== "video") {
      break;
    }

    levels.push({ depth, scenario, question, feedback });

    const nextId = feedback.nextNodeId;
    scenarioId = nextId.startsWith("scenario-") ? nextId : null;
  }

  return levels;
})();

const summaryNode = episodeMap.get("summary");

export function NextGenVideoLessonPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<UnityStatus>("checking");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [completedBeatIds, setCompletedBeatIds] = useState<string[]>([]);

  useEffect(() => {
    let disposed = false;
    let unityInstance: UnityInstance | null = null;

    const loaderLooksValid = async () => {
      const manifestResponse = await fetch(unityManifestUrl, {
        cache: "no-store",
      }).catch(() => null);

      if (!manifestResponse?.ok) {
        return false;
      }

      const manifest = (await manifestResponse.json().catch(() => null)) as {
        hasBuild?: boolean;
      } | null;

      if (!manifest?.hasBuild) {
        return false;
      }

      const response = await fetch(unityLoaderUrl, {
        method: "GET",
        cache: "no-store",
      }).catch(() => null);

      if (!response?.ok) {
        return false;
      }

      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("text/html")) {
        return false;
      }

      const source = await response.clone().text().catch(() => "");
      return source.includes("createUnityInstance");
    };

    const loadScript = () =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${unityLoaderUrl}"]`,
        );
        if (existing) {
          if (window.createUnityInstance) {
            resolve();
            return;
          }
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener("error", () => reject(new Error("Unity loader failed.")), {
            once: true,
          });
          return;
        }

        const script = document.createElement("script");
        script.src = unityLoaderUrl;
        script.async = true;
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => reject(new Error("Unity loader failed.")), {
          once: true,
        });
        document.body.appendChild(script);
      });

    const bootUnity = async () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      setStatus("checking");
      setError(null);

      if (!(await loaderLooksValid())) {
        if (!disposed) {
          setStatus("missing");
        }
        return;
      }

      setStatus("loading");
      await loadScript();

      if (!window.createUnityInstance) {
        throw new Error("Unity loader did not expose createUnityInstance.");
      }

      unityInstance = await window.createUnityInstance(canvas, unityConfig, (value) => {
        if (!disposed) {
          setProgress(Math.round(value * 100));
        }
      });

      if (disposed) {
        await unityInstance.Quit?.();
        return;
      }

      setProgress(100);
      setStatus("ready");
    };

    void bootUnity().catch((loadError: unknown) => {
      if (disposed) {
        return;
      }
      setError(loadError instanceof Error ? loadError.message : String(loadError));
      setStatus("error");
    });

    return () => {
      disposed = true;
      void unityInstance?.Quit?.();
    };
  }, []);

  const statusCopy = useMemo(() => {
    if (status === "ready") {
      return "Unity build active";
    }
    if (status === "loading") {
      return `Loading Unity ${progress}%`;
    }
    if (status === "missing") {
      return "Browser demo active";
    }
    if (status === "error") {
      return "Unity unavailable - browser demo active";
    }
    return "Checking local Unity build";
  }, [progress, status]);

  const showBrowserDemo = status === "missing" || status === "error" || status === "checking";

  return (
    <div className="min-h-[calc(100dvh-3.5rem)] overflow-x-hidden bg-[#070713] text-white">
      <style>
        {`
          @keyframes achievement-pop {
            0% {
              opacity: 0;
              transform: translate3d(0, 16px, 0) scale(0.96);
            }
            10% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
            }
            82% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
            }
            100% {
              opacity: 0;
              transform: translate3d(0, 16px, 0) scale(0.96);
            }
          }

          @media (min-width: 640px) {
            @keyframes achievement-pop {
              0% {
                opacity: 0;
                transform: translate3d(28px, -12px, 0) scale(0.96);
              }
              10% {
                opacity: 1;
                transform: translate3d(0, 0, 0) scale(1);
              }
              82% {
                opacity: 1;
                transform: translate3d(0, 0, 0) scale(1);
              }
              100% {
                opacity: 0;
                transform: translate3d(28px, -12px, 0) scale(0.96);
              }
            }
          }

          @keyframes achievement-timer {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }

          @keyframes episode-complete-pop {
            0% {
              transform: scale(1);
            }
            45% {
              transform: scale(1.025);
            }
            100% {
              transform: scale(1);
            }
          }
        `}
      </style>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_20%,rgba(30,187,240,0.22),transparent_32%),radial-gradient(circle_at_82%_18%,rgba(244,92,15,0.18),transparent_28%),linear-gradient(135deg,#080614_0%,#170f3f_52%,#06060f_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:44px_44px] opacity-25" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:gap-8 sm:px-6 sm:py-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 max-w-3xl">
            <div className="mb-4 flex flex-wrap items-center gap-2 sm:mb-5">
              <Badge tone="ai">Local Unity WebGL POC</Badge>
              <Badge tone="warning">Internal only</Badge>
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/15 bg-white/8 px-2.5 py-1 text-[11px] font-semibold text-cyan-100 sm:text-xs">
                <RadioTower className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{statusCopy}</span>
              </span>
            </div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.24em] text-cyan-200 sm:mb-3 sm:text-xs sm:tracking-[0.32em]">
              Next Gen Video Lesson
            </p>
            <h1 className="max-w-3xl text-3xl font-black leading-[1.02] tracking-[-0.04em] text-white sm:text-4xl md:text-5xl lg:text-6xl lg:leading-[0.95]">
              Ethical dilemmas, played like an interactive episode.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-white/72 sm:mt-5 sm:text-base sm:leading-7">
              A local-first Unity WebGL host with a playable browser demo until the Unity export is
              dropped in. The current demo uses the MP4 files from <code className="break-all">docs/videos</code>.
            </p>
          </div>
          <div className="grid w-full shrink-0 gap-3 rounded-2xl border border-white/12 bg-black/30 p-4 shadow-2xl shadow-black/30 backdrop-blur sm:max-w-sm lg:w-auto lg:min-w-[260px]">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-white/48">
              Chapter
              <span>01/03</span>
            </div>
            <div className="text-xl font-black text-white">Ethical Dilemmas</div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-1/3 rounded-full bg-cyan-300" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-4 px-4 py-6 sm:gap-6 sm:px-6 sm:py-8 lg:grid-cols-[minmax(0,1fr)_min(100%,20rem)] lg:items-start xl:grid-cols-[minmax(0,1fr)_22.5rem]">
        <div className="min-w-0 overflow-hidden rounded-2xl border border-white/12 bg-black shadow-[0_40px_120px_-60px_rgba(30,187,240,0.85)] sm:rounded-[28px]">
          <div className="flex flex-col gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <MonitorPlay className="mt-0.5 h-5 w-5 shrink-0 text-cyan-200 sm:mt-0" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-white">
                  {showBrowserDemo ? "Playable Browser Demo" : "Unity Player Stage"}
                </div>
                <div className="hidden text-xs text-white/48 md:block">
                  Unity loader slot: <code className="break-all">{unityLoaderUrl}</code>
                </div>
              </div>
            </div>
            <StatusPill status={status} statusCopy={statusCopy} />
          </div>

          <div className="relative min-h-0 bg-[#050508]">
            <canvas
              ref={canvasRef}
              id="unity-next-gen-video-lesson"
              className={cn(
                "aspect-video h-full w-full bg-black",
                status === "ready" || status === "loading" ? "block" : "hidden",
              )}
            />

            {showBrowserDemo && (
              <PlayableEpisode
                error={error}
                onBeatCompleted={(beatId) => {
                  setCompletedBeatIds((current) =>
                    current.includes(beatId) ? current : [...current, beatId],
                  );
                }}
                onResetCompletedBeats={() => setCompletedBeatIds([])}
              />
            )}

            {status === "loading" && (
              <div className="absolute inset-x-4 bottom-4 rounded-2xl border border-white/12 bg-black/75 p-4 backdrop-blur sm:inset-x-8 sm:bottom-8">
                <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.22em] text-white/55">
                  Loading episode
                  <span>{progress}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-cyan-300 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="min-w-0 lg:sticky lg:top-6">
          <EpisodeFlow completedBeatIds={completedBeatIds} />
        </aside>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pb-8 sm:px-6 sm:pb-10">
        <DecisionTreeMap completedBeatIds={completedBeatIds} />
      </section>
    </div>
  );
}

function PlayableEpisode({
  error,
  onBeatCompleted,
  onResetCompletedBeats,
}: {
  error: string | null;
  onBeatCompleted: (beatId: string) => void;
  onResetCompletedBeats: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentNodeId, setCurrentNodeId] = useState("scenario-1");
  const [score, setScore] = useState(0);
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [choiceResult, setChoiceResult] = useState<ChoiceResult | null>(null);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMutedForAutoplay, setIsMutedForAutoplay] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [choiceMode, setChoiceMode] = useState<ChoiceMode>("classic");
  const [hasOpenedDoomMode, setHasOpenedDoomMode] = useState(false);
  const [lastDoomQuestion, setLastDoomQuestion] = useState<QuestionNode | null>(null);

  const node = episodeMap.get(currentNodeId) ?? episodeNodes[0];
  const questionNode = node.type === "question" ? node : null;
  const doomHostQuestion = questionNode ?? lastDoomQuestion;
  const videoNodes = episodeNodes.filter((entry): entry is VideoNode => entry.type === "video");
  const completedVideoCount = videoNodes.findIndex((entry) => entry.id === currentNodeId);
  const progress = node.type === "summary"
    ? 100
    : Math.max(8, Math.round(((completedVideoCount + 1) / videoNodes.length) * 100));
  const isDoomVisible = questionNode !== null && choiceMode === "doom";
  const shouldKeepDoomMounted = hasOpenedDoomMode && doomHostQuestion !== null;
  const doomOptions: DoomChoiceOption[] = useMemo(
    () =>
      doomHostQuestion?.options.map((option, index) => ({
        index,
        label: option.label,
        score: option.score,
        tone: option.tone,
        feedbackTitle: option.feedbackTitle,
        feedbackBody: option.feedbackBody,
      })) ?? [],
    [doomHostQuestion],
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.load();
  }, [currentNodeId]);

  useEffect(() => {
    if (node.type !== "video") {
      setShouldAutoplay(false);
      setIsMutedForAutoplay(false);
      setPlaybackBlocked(false);
    }
  }, [node.type]);

  useEffect(() => {
    if (questionNode) {
      setLastDoomQuestion(questionNode);
    }
  }, [questionNode]);

  useEffect(() => {
    if (isDoomVisible) {
      setHasOpenedDoomMode(true);
    }
  }, [isDoomVisible]);

  useEffect(() => {
    if (!choiceResult) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setChoiceResult(null);
    }, 4200);

    return () => window.clearTimeout(timeoutId);
  }, [choiceResult]);

  useEffect(() => {
    let buffer = "";

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (event.key === "Escape") {
        setChoiceMode("classic");
        buffer = "";
        return;
      }

      if (!/^[a-z]$/i.test(event.key)) {
        return;
      }

      buffer = `${buffer}${event.key.toUpperCase()}`.slice(-4);
      if (buffer === "DOOM") {
        setChoiceMode("doom");
        setHasOpenedDoomMode(true);
        buffer = "";
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const advance = useCallback((nextNodeId: string) => {
    setCurrentNodeId(nextNodeId);
  }, []);

  const playCurrentVideo = async () => {
    const video = videoRef.current;
    if (!video || node.type !== "video" || !shouldAutoplay) {
      return;
    }

    setPlaybackBlocked(false);

    try {
      video.muted = false;
      setIsMutedForAutoplay(false);
      await video.play();
      setShouldAutoplay(false);
    } catch {
      try {
        video.muted = true;
        setIsMutedForAutoplay(true);
        await video.play();
        setShouldAutoplay(false);
      } catch {
        setPlaybackBlocked(true);
      }
    }
  };

  const startEpisode = async () => {
    const video = videoRef.current;
    if (!video || node.type !== "video") {
      return;
    }

    setHasStarted(true);
    setPlaybackBlocked(false);
    setIsMutedForAutoplay(false);

    try {
      video.muted = false;
      await video.play();
      setShouldAutoplay(false);
    } catch {
      setShouldAutoplay(true);
    }
  };

  const reset = () => {
    setCurrentNodeId("scenario-1");
    setScore(0);
    setSelectedChoices([]);
    setChoiceResult(null);
    setShouldAutoplay(false);
    setHasStarted(false);
    setIsMutedForAutoplay(false);
    setPlaybackBlocked(false);
    setChoiceMode("classic");
    setHasOpenedDoomMode(false);
    setLastDoomQuestion(null);
    onResetCompletedBeats();
  };

  const pickOption = useCallback((option: QuestionNode["options"][number]) => {
    const result: ChoiceResult = {
      label: option.label,
      score: option.score,
      tone: option.tone,
      feedbackTitle: option.feedbackTitle,
      feedbackBody: option.feedbackBody,
    };

    setScore((current) => current + option.score);
    setSelectedChoices((current) => [...current, `${option.tone}: ${option.label}`]);
    setChoiceResult(result);
    setIsMutedForAutoplay(false);
    setShouldAutoplay(true);
    advance(option.nextNodeId);
  }, [advance]);

  const handleDoomSelection = useCallback((optionIndex: number) => {
    const option = questionNode?.options[optionIndex];
    if (option) {
      setChoiceMode("classic");
      pickOption(option);
    }
  }, [pickOption, questionNode]);

  const doomOverlay = shouldKeepDoomMounted && doomHostQuestion ? (
    <div
      className={cn(
        "absolute inset-0 z-30 transition-opacity",
        isDoomVisible ? "pointer-events-auto opacity-100" : "pointer-events-none -z-10 opacity-0",
      )}
      aria-hidden={!isDoomVisible}
    >
      <DoomUnityArena
        questionId={doomHostQuestion.id}
        options={doomOptions}
        onSelectOption={handleDoomSelection}
        onExitDoomMode={() => setChoiceMode("classic")}
      />
    </div>
  ) : null;

  let content: ReactNode;

  if (node.type === "summary") {
    const outcome = score >= 5
      ? "Strong ethical judgment"
      : score >= 3
        ? "Good start, review the feedback moments"
        : "Needs more practice with ethical decision-making";

    content = (
      <div className="grid min-h-[min(70dvh,560px)] place-items-center bg-[radial-gradient(circle_at_center,rgba(30,187,240,0.16),transparent_42%),#050508] p-4 sm:min-h-[560px] sm:p-8">
        <div className="max-w-2xl text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-cyan-200/20 bg-cyan-200/10 sm:mb-5 sm:h-16 sm:w-16">
            <CheckCircle2 className="h-7 w-7 text-cyan-200 sm:h-8 sm:w-8" />
          </div>
          <p className="mb-2 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-200 sm:mb-3 sm:text-xs sm:tracking-[0.28em]">
            Episode complete
          </p>
          <h2 className="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">{outcome}</h2>
          <p className="mt-4 text-white/65">
            Score: <strong className="text-white">{score}/6</strong>. The Unity version can use this
            same shape for branching, consequence videos, and end-of-episode feedback.
          </p>
          <div className="mt-6 grid gap-2 text-left">
            {selectedChoices.map((choice, index) => (
              <div key={`${choice}-${index}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-sm text-white/72">
                <span className="mr-2 font-black text-cyan-200">Decision {index + 1}</span>
                {choice}
              </div>
            ))}
          </div>
          <button
            onClick={reset}
            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-cyan-300 px-4 py-2 text-sm font-black text-[#070713] transition hover:bg-cyan-200"
          >
            <RotateCcw className="h-4 w-4" />
            Replay episode
          </button>
        </div>
      </div>
    );
  } else if (node.type === "question") {
    content = (
      <div className="min-h-[min(70dvh,560px)] bg-[linear-gradient(135deg,rgba(23,15,63,0.95),rgba(5,5,8,1))] sm:min-h-[560px]">
        {choiceMode === "doom" ? (
          <div className="min-h-[min(70dvh,560px)] bg-[#120607] sm:min-h-[560px]" />
        ) : (
          <div className="grid min-h-[min(70dvh,560px)] gap-6 p-4 sm:min-h-[560px] sm:gap-8 sm:p-6 lg:grid-cols-[minmax(0,1fr)_min(100%,26.25rem)] lg:p-8">
            <div className="flex flex-col justify-end">
              <p className="mb-3 text-[11px] font-black uppercase tracking-[0.22em] text-orange-200">
                {node.label}
              </p>
              <h2 className="max-w-3xl text-2xl font-black leading-tight tracking-tight text-white sm:text-3xl lg:text-5xl xl:text-6xl">
                {node.title}
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-7 text-white/70 sm:mt-5 sm:text-lg sm:leading-8">
                {node.prompt}
              </p>
            </div>
            <div className="flex flex-col justify-center gap-2.5 sm:gap-3">
              {node.options.map((option, index) => (
                <button
                  key={option.label}
                  onClick={() => pickOption(option)}
                  className="group rounded-2xl border border-white/12 bg-black/35 p-3.5 text-left transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-200/10 sm:p-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-2 sm:items-center">
                    <span className="text-[11px] font-black uppercase tracking-[0.22em] text-cyan-200">
                      Choice {index + 1}
                    </span>
                    <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold text-white/60 group-hover:bg-cyan-200 group-hover:text-[#070713]">
                      {option.tone}
                    </span>
                  </div>
                  <div className="text-sm font-black text-white sm:text-base">{option.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  } else {
    content = (
    <div className="bg-black">
      <div className="relative">
        <video
          key={node.id}
          ref={videoRef}
          className="aspect-video h-full w-full bg-black object-contain"
          controls
          muted={isMutedForAutoplay}
          playsInline
          preload="auto"
          autoPlay={shouldAutoplay}
          onLoadedData={() => void playCurrentVideo()}
          onCanPlay={() => void playCurrentVideo()}
          onPlay={() => setShouldAutoplay(false)}
          onEnded={() => {
            onBeatCompleted(node.id);
            setIsMutedForAutoplay(false);
            setShouldAutoplay(true);
            advance(node.nextNodeId);
          }}
        >
          <source src={videoPath(node.fileName)} type="video/mp4" />
        </video>
        {!hasStarted && currentNodeId === "scenario-1" && (
          <div className="absolute inset-0 z-20 grid place-items-center bg-[radial-gradient(circle_at_center,rgba(30,187,240,0.18),transparent_44%),rgba(5,5,8,0.88)] p-4 text-center backdrop-blur-sm sm:p-8">
            <div className="max-w-xl px-1">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-cyan-200/25 bg-cyan-200/12 text-cyan-100 sm:mb-5 sm:h-16 sm:w-16">
                <Play className="h-7 w-7 fill-current sm:h-8 sm:w-8" />
              </div>
              <p className="mb-2 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-200 sm:mb-3 sm:text-xs sm:tracking-[0.28em]">
                Sound-on episode
              </p>
              <h2 className="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
                Start the interactive lesson
              </h2>
              <p className="mt-2 text-sm leading-6 text-white/65 sm:mt-3">
                One click unlocks audio playback for the episode, then the scenario and feedback
                videos can continue automatically.
              </p>
              <button
                onClick={() => void startEpisode()}
                className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-cyan-300 px-5 py-3 text-sm font-black text-[#07111f] shadow-2xl shadow-cyan-950/35 transition hover:-translate-y-0.5 hover:bg-cyan-200"
              >
                <Play className="h-4 w-4 fill-current" />
                Start episode
              </button>
            </div>
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 via-black/50 to-transparent p-3 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-2 sm:items-center sm:gap-3">
            <div className="min-w-0 pr-2">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-200 sm:text-xs sm:tracking-[0.28em]">
                {node.label}
              </p>
              <h2 className="mt-0.5 text-lg font-black text-white sm:mt-1 sm:text-2xl">{node.title}</h2>
              <p className="line-clamp-2 text-xs text-white/60 sm:line-clamp-none sm:text-sm">{node.subtitle}</p>
            </div>
            <div className="shrink-0 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-[11px] font-bold text-white/70 sm:px-3 sm:text-xs">
              Score {score}/6
            </div>
          </div>
        </div>
        {choiceResult && (
          <div className="pointer-events-none absolute inset-x-3 bottom-24 z-10 sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-6 sm:w-[min(440px,calc(100%-3rem))] animate-[achievement-pop_4200ms_ease-in-out_forwards]">
            <div className="overflow-hidden rounded-2xl border border-cyan-200/35 bg-[#07111f]/95 shadow-[0_24px_80px_-30px_rgba(30,187,240,0.95)] backdrop-blur">
              <div className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-cyan-200/35 bg-cyan-200/15 text-cyan-100 shadow-inner shadow-cyan-100/10 sm:h-14 sm:w-14">
                  <Sparkles className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <p className="truncate text-[11px] font-black uppercase tracking-[0.22em] text-cyan-200">
                      Achievement unlocked
                    </p>
                    <span className="rounded-full bg-cyan-200 px-2 py-0.5 text-[11px] font-black text-[#07111f]">
                      {choiceResult.score === 2 ? "+2" : choiceResult.score === 1 ? "+1" : "+0"}
                    </span>
                  </div>
                  <h3 className="truncate text-lg font-black text-white">{choiceResult.feedbackTitle}</h3>
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-white/62">
                    {choiceResult.tone}: {choiceResult.feedbackBody}
                  </p>
                </div>
              </div>
              <div className="h-1 bg-white/10">
                <div className="h-full animate-[achievement-timer_4200ms_linear_forwards] bg-cyan-300" />
              </div>
            </div>
          </div>
        )}
        {(isMutedForAutoplay || playbackBlocked) && (
          <div className="absolute inset-x-3 bottom-3 z-10 max-w-none rounded-2xl border border-cyan-200/25 bg-[#07111f]/90 p-3 shadow-2xl shadow-cyan-950/35 backdrop-blur sm:inset-x-auto sm:bottom-6 sm:left-6 sm:max-w-sm sm:p-4">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-200">
              Autoplay protection
            </p>
            <p className="mt-1 text-sm leading-5 text-white/68">
              The browser started the clip muted. Tap once to restore audio.
            </p>
            <button
              onClick={() => {
                const video = videoRef.current;
                if (!video) {
                  return;
                }

                video.muted = false;
                setIsMutedForAutoplay(false);
                setPlaybackBlocked(false);
                void video.play();
              }}
              className="mt-3 rounded-lg bg-cyan-300 px-3 py-1.5 text-xs font-black text-[#07111f] transition hover:bg-cyan-200"
            >
              Unmute and continue
            </button>
          </div>
        )}
        {error && (
          <div className="absolute inset-x-3 top-20 max-w-none rounded-xl border border-orange-300/25 bg-orange-400/10 p-3 text-xs text-orange-50 sm:inset-x-auto sm:left-5 sm:top-28 sm:max-w-md">
            Unity loader note: {error}
          </div>
        )}
      </div>
      <div className="border-t border-white/10 bg-[#080814] p-3 sm:p-5">
        <div className="rounded-2xl border border-white/12 bg-white/[0.04] p-3 sm:p-4">
          <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.22em] text-white/55">
            Browser demo progress
            <span>{progress}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-cyan-300 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <p className="text-xs text-white/55">
              Feedback videos auto-play after choices. Scenario videos advance when they end.
            </p>
            <button
              onClick={() => {
                onBeatCompleted(node.id);
                setChoiceResult(null);
                setIsMutedForAutoplay(false);
                setShouldAutoplay(true);
                advance(node.nextNodeId);
              }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-cyan-300 hover:text-[#070713] sm:w-auto sm:py-1.5"
            >
              Skip to next beat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  }

  return (
    <div className="relative">
      {content}
      {doomOverlay}
    </div>
  );
}

function StatusPill({
  status,
  statusCopy,
}: {
  status: UnityStatus;
  statusCopy: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold sm:text-xs",
        status === "ready"
          ? "bg-emerald-400/15 text-emerald-200"
          : status === "error"
            ? "bg-orange-400/15 text-orange-100"
            : "bg-cyan-300/15 text-cyan-100",
      )}
    >
      {status === "loading" || status === "checking" ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : status === "ready" ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <AlertTriangle className="h-3.5 w-3.5" />
      )}
      <span className="truncate">{statusCopy}</span>
    </span>
  );
}

const TREE_STROKE = "rgba(255,255,255,0.22)";
const TREE_STROKE_ACTIVE = "rgba(34,211,238,0.5)";

function DecisionTreeMap({ completedBeatIds }: { completedBeatIds: string[] }) {
  const rootLevel = decisionTreeLevels[0];
  const level2 = decisionTreeLevels[1];
  const level3 = decisionTreeLevels[2];

  if (!rootLevel || !level2 || !level3) {
    return null;
  }

  return (
    <section className="w-full rounded-2xl border border-white/12 bg-white/[0.04] p-4 sm:rounded-3xl sm:p-5">
      <div className="mb-3 flex items-center gap-2 text-cyan-100">
        <GitBranch className="h-4 w-4 shrink-0" />
        <h2 className="text-sm font-black text-white sm:text-base">Decision Tree</h2>
      </div>
      <p className="mb-4 max-w-3xl text-xs leading-5 text-white/50 sm:text-sm">
        Each choice plays its own feedback, then opens a distinct scenario branch. Three decision
        layers multiply the paths — up to twenty-seven endings, all converging on one summary.
      </p>

      <div className="w-full overflow-x-auto">
        <div className="mx-auto flex w-full min-w-[36rem] max-w-6xl flex-col items-center px-1 sm:min-w-0">
          <DecisionTreeLevelSegment level={rootLevel} completedBeatIds={completedBeatIds} />

          <FeedbackToScenariosRow
            parentOptions={rootLevel.question.options}
            nextScenario={level2.scenario}
          />

          <div className="grid w-full grid-cols-3 gap-3 sm:gap-5">
            {rootLevel.question.options.map((parentOption, trackIndex) => (
              <DecisionTreeTrackColumn
                key={`track-${trackIndex}`}
                pathTone={parentOption.tone}
                level={level2}
                nextLevel={level3}
                completedBeatIds={completedBeatIds}
              />
            ))}
          </div>

          {summaryNode?.type === "summary" && (
            <>
              <TracksToSummaryBridge trackCount={rootLevel.question.options.length} />
              <div className="w-full max-w-sm px-2">
                <TreeNodeCard
                  kind="summary"
                  label="Summary"
                  title={summaryNode.title}
                  completed={completedBeatIds.includes("feedback-3")}
                  showEntryPort
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-3">
        <TreeLegendSwatch kind="scenario" label="Scenario" />
        <TreeLegendSwatch kind="decision" label="Decision" />
        <TreeLegendSwatch kind="option" label="Choice" />
        <TreeLegendSwatch kind="feedback" label="Feedback" />
      </div>
    </section>
  );
}

function DecisionTreeLevelSegment({
  level,
  completedBeatIds,
}: {
  level: DecisionTreeLevel;
  completedBeatIds: string[];
}) {
  const scenarioDone = completedBeatIds.includes(level.scenario.id);
  const feedbackDone = completedBeatIds.includes(level.feedback.id);

  return (
    <div className="flex w-full max-w-5xl flex-col items-center">
      <div className="w-full max-w-xs sm:max-w-sm">
        <TreeNodeCard
          kind="scenario"
          label={level.scenario.label}
          title={level.scenario.title}
          completed={scenarioDone}
          showEntryPort={level.depth > 1}
        />
      </div>

      <TreeConnector />

      <div className="w-full max-w-md px-2">
        <TreeNodeCard
          kind="decision"
          label={level.question.label}
          title={level.question.title}
          completed={false}
        />
      </div>

      <TreeDecisionFanOut />

      <div className="grid w-full grid-cols-3 gap-2 px-1 sm:gap-4 sm:px-2">
        {level.question.options.map((option, optionIndex) => (
          <div
            key={`${level.question.id}-${optionIndex}`}
            className="flex min-w-0 flex-col items-center"
          >
            <TreeNodeCard
              kind="option"
              label={`Choice ${optionIndex + 1}`}
              title={option.tone}
              subtitle={truncateTreeLabel(option.label, 36)}
              completed={false}
            />
            <TreeConnector compact />
            <TreeNodeCard
              kind="feedback"
              label={level.feedback.label}
              title={option.feedbackTitle}
              subtitle={level.feedback.title}
              completed={feedbackDone}
              showExitPort
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function TreeDecisionFanOut() {
  return (
    <svg
      className="h-8 w-full max-w-5xl sm:h-10"
      viewBox="0 0 900 40"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M 450 0 L 450 14"
        stroke={TREE_STROKE_ACTIVE}
        strokeWidth="1.75"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M 150 14 L 750 14"
        stroke={TREE_STROKE}
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M 150 14 L 150 40"
        stroke={TREE_STROKE}
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M 450 14 L 450 40"
        stroke={TREE_STROKE_ACTIVE}
        strokeWidth="1.75"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M 750 14 L 750 40"
        stroke={TREE_STROKE}
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

type QuestionOption = QuestionNode["options"][number];

function FeedbackToScenariosRow({
  parentOptions,
  nextScenario,
}: {
  parentOptions: QuestionOption[];
  nextScenario: VideoNode;
}) {
  return (
    <div className="w-full max-w-6xl py-1 sm:py-2">
      <div className="grid w-full grid-cols-3 gap-3 sm:gap-5">
        {parentOptions.map((option, index) => (
          <div key={`${option.tone}-${index}`} className="flex min-w-0 flex-col items-center">
            <FeedbackToScenarioLink emphasized={index === 1} />
            <TreeNodeCard
              kind="scenario"
              label={nextScenario.label}
              title={nextScenario.title}
              subtitle={`${option.tone} path`}
              showEntryPort
            />
          </div>
        ))}
      </div>
      <p className="mt-2 text-center text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">
        Each feedback opens its own {nextScenario.label}
      </p>
    </div>
  );
}

function DecisionTreeTrackColumn({
  pathTone,
  level,
  nextLevel,
  completedBeatIds,
}: {
  pathTone: string;
  level: DecisionTreeLevel;
  nextLevel: DecisionTreeLevel;
  completedBeatIds: string[];
}) {
  const scenarioDone = completedBeatIds.includes(level.scenario.id);
  const feedbackDone = completedBeatIds.includes(level.feedback.id);
  const nextScenarioDone = completedBeatIds.includes(nextLevel.scenario.id);

  return (
    <div className="flex min-w-0 flex-col items-center rounded-2xl border border-white/10 bg-black/15 p-2 sm:p-3">
      <div className="w-full">
        <TreeNodeCard
          kind="scenario"
          label={level.scenario.label}
          title={level.scenario.title}
          subtitle={`${pathTone} path`}
          completed={scenarioDone}
          showEntryPort
        />
      </div>

      <TreeConnector />

      <div className="w-full">
        <TreeNodeCard
          kind="decision"
          label={level.question.label}
          title={level.question.title}
          completed={false}
        />
      </div>

      <TreeDecisionFanOut />

      <div className="grid w-full grid-cols-3 gap-1.5 sm:gap-2">
        {level.question.options.map((option, optionIndex) => (
          <div
            key={`${level.question.id}-${pathTone}-${optionIndex}`}
            className="flex min-w-0 flex-col items-center"
          >
            <TreeNodeCard
              kind="option"
              label={`Choice ${optionIndex + 1}`}
              title={option.tone}
              subtitle={truncateTreeLabel(option.label, 28)}
              completed={false}
            />
            <TreeConnector compact />
            <TreeNodeCard
              kind="feedback"
              label={level.feedback.label}
              title={option.feedbackTitle}
              subtitle={level.feedback.title}
              completed={feedbackDone}
              showExitPort
            />
          </div>
        ))}
      </div>

      <div className="mt-2 w-full border-t border-white/10 pt-3">
        <p className="mb-2 text-center text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">
          {level.feedback.label} → {nextLevel.scenario.label}
        </p>
        <div className="grid w-full grid-cols-3 gap-1.5 sm:gap-2">
          {level.question.options.map((option, index) => (
            <div
              key={`${pathTone}-next-${option.tone}-${index}`}
              className="flex min-w-0 flex-col items-center"
            >
              <FeedbackToScenarioLink emphasized={index === 1} />
              <TreeNodeCard
                kind="scenario"
                label={nextLevel.scenario.label}
                title={nextLevel.scenario.title}
                subtitle={`${pathTone} › ${option.tone}`}
                completed={nextScenarioDone}
                showEntryPort
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeedbackToScenarioLink({ emphasized = false }: { emphasized?: boolean }) {
  return (
    <svg
      className="h-10 w-full sm:h-12"
      viewBox="0 0 100 48"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M 50 4 L 50 42"
        stroke={emphasized ? TREE_STROKE_ACTIVE : TREE_STROKE}
        strokeWidth={emphasized ? 1.75 : 1.5}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx="50"
        cy="44"
        r="3"
        fill="rgba(34,211,238,0.65)"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function TracksToSummaryBridge({ trackCount }: { trackCount: number }) {
  const columnCenters =
    trackCount === 3 ? [150, 450, 750] : Array.from({ length: trackCount }, (_, i) => ((i + 1) / (trackCount + 1)) * 900);

  return (
    <div className="flex w-full max-w-6xl flex-col items-center py-2 sm:py-3">
      <svg
        className="h-16 w-full sm:h-20"
        viewBox="0 0 900 72"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden
        xmlns="http://www.w3.org/2000/svg"
      >
        {columnCenters.map((x, index) => (
          <path
            key={x}
            d={`M ${x} 8 L ${x} 38 L 450 58`}
            stroke={index === 1 ? TREE_STROKE_ACTIVE : TREE_STROKE}
            strokeWidth={index === 1 ? 1.75 : 1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <circle
          cx="450"
          cy="62"
          r="3.5"
          fill="rgba(34,211,238,0.65)"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span className="mt-1 text-center text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">
        All paths → Summary
      </span>
    </div>
  );
}

function TreeConnector({ label, compact = false }: { label?: string; compact?: boolean }) {
  return (
    <div className={cn("flex flex-col items-center", compact ? "py-1" : "py-1.5")}>
      <div className={cn("w-px bg-white/20", compact ? "h-2" : "h-3")} />
      {label && (
        <span className="mt-1 text-[9px] font-bold uppercase tracking-[0.16em] text-white/35">
          {label}
        </span>
      )}
    </div>
  );
}

type TreeNodeKind = "scenario" | "decision" | "option" | "feedback" | "summary";

function TreeNodeCard({
  kind,
  label,
  title,
  subtitle,
  completed = false,
  showExitPort = false,
  showEntryPort = false,
}: {
  kind: TreeNodeKind;
  label: string;
  title: string;
  subtitle?: string;
  completed?: boolean;
  showExitPort?: boolean;
  showEntryPort?: boolean;
}) {
  const Icon = treeNodeIcon[kind];

  return (
    <div
      className={cn(
        "relative w-full min-w-0 rounded-xl border p-2 text-center transition-colors",
        treeNodeSurface[kind],
        completed && "border-emerald-300/45 bg-emerald-400/12",
      )}
    >
      {showEntryPort && (
        <span
          className="absolute -top-1.5 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-cyan-200/50 bg-cyan-300/80 shadow-[0_0_10px_rgba(30,187,240,0.65)]"
          aria-hidden
        />
      )}
      {showExitPort && (
        <span
          className="absolute -bottom-1.5 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-cyan-200/50 bg-cyan-300/80 shadow-[0_0_10px_rgba(30,187,240,0.65)]"
          aria-hidden
        />
      )}
      <div className="mb-1 flex items-center justify-center gap-1">
        <Icon className={cn("h-3 w-3 shrink-0", treeNodeIconTone[kind])} />
        <span
          className={cn(
            "text-[9px] font-black uppercase tracking-[0.14em]",
            completed ? "text-emerald-200" : treeNodeLabelTone[kind],
          )}
        >
          {label}
        </span>
      </div>
      <div
        className={cn(
          "text-[11px] font-black leading-snug text-white",
          kind === "option" && "text-[10px]",
        )}
      >
        {title}
      </div>
      {subtitle && (
        <p className="mt-0.5 line-clamp-2 text-[9px] leading-4 text-white/45">{subtitle}</p>
      )}
    </div>
  );
}

function TreeLegendSwatch({ kind, label }: { kind: TreeNodeKind; label: string }) {
  const Icon = treeNodeIcon[kind];

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/25 px-2 py-1 text-[10px] font-semibold text-white/55">
      <Icon className={cn("h-3 w-3", treeNodeIconTone[kind])} />
      {label}
    </span>
  );
}

function truncateTreeLabel(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1)}…`;
}

const treeNodeIcon: Record<TreeNodeKind, typeof Video> = {
  scenario: Video,
  decision: GitBranch,
  option: MessageSquare,
  feedback: MonitorPlay,
  summary: CheckCircle2,
};

const treeNodeSurface: Record<TreeNodeKind, string> = {
  scenario: "border-cyan-300/25 bg-cyan-400/10",
  decision: "border-orange-300/25 bg-orange-400/10",
  option: "border-white/12 bg-white/[0.04]",
  feedback: "border-violet-300/25 bg-violet-400/10",
  summary: "border-emerald-300/25 bg-emerald-400/10",
};

const treeNodeIconTone: Record<TreeNodeKind, string> = {
  scenario: "text-cyan-200",
  decision: "text-orange-200",
  option: "text-white/55",
  feedback: "text-violet-200",
  summary: "text-emerald-200",
};

const treeNodeLabelTone: Record<TreeNodeKind, string> = {
  scenario: "text-cyan-200",
  decision: "text-orange-200",
  option: "text-white/45",
  feedback: "text-violet-200",
  summary: "text-emerald-200",
};

function EpisodeFlow({ completedBeatIds }: { completedBeatIds: string[] }) {
  return (
    <section className="rounded-2xl border border-white/12 bg-white/[0.04] p-4 sm:rounded-3xl sm:p-5">
      <div className="mb-3 flex items-center gap-2 text-cyan-100">
        <Clapperboard className="h-4 w-4 shrink-0" />
        <h2 className="text-sm font-black text-white sm:text-base">Episode Flow</h2>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
        {episodeNodes
          .filter((node): node is VideoNode => node.type === "video")
          .map((beat, index) => {
            const isCompleted = completedBeatIds.includes(beat.id);

            return (
              <div
                key={beat.id}
                className={cn(
                  "rounded-2xl border p-3 transition-all duration-500",
                  isCompleted
                    ? "animate-[episode-complete-pop_520ms_ease-out] border-emerald-300/45 bg-emerald-400/12 shadow-lg shadow-emerald-950/30"
                    : "border-white/10 bg-black/25",
                )}
              >
                <div className="mb-1 flex items-center justify-between gap-3">
                  <span
                    className={cn(
                      "text-[11px] font-black uppercase tracking-[0.18em]",
                      isCompleted ? "text-emerald-200" : "text-cyan-200",
                    )}
                  >
                    {beat.label}
                  </span>
                  <span
                    className={cn(
                      "grid h-6 w-6 place-items-center rounded-full text-[11px] font-black transition-all duration-500",
                      isCompleted
                        ? "bg-emerald-300 text-[#07111f]"
                        : "bg-white/5 text-white/35",
                    )}
                  >
                    {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className={cn("text-sm font-bold", isCompleted ? "text-emerald-50" : "text-white")}>
                  {beat.title}
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
}

